import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, BadgeCheck, CheckCircle2, Trophy, GraduationCap } from "lucide-react";
import { IMAGES, WHATSAPP_LINK, STATS } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: EASE, delay: 0.2 + i * 0.13 },
  }),
};

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: EASE, delay },
});

export default function Hero({ scrollTo }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "8%"]);
  const watermarkY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section id="top" ref={ref} data-testid="hero" className="relative overflow-hidden pt-[72px]">
      <motion.span
        aria-hidden="true"
        style={{ y: watermarkY }}
        className="pointer-events-none absolute -top-10 right-0 select-none font-serif text-[26vw] italic leading-none text-brand-green/[0.05]"
      >
        ABHI
      </motion.span>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 pb-20 pt-14 md:pt-20 lg:grid-cols-12 lg:gap-10">
        <div className="relative z-10 lg:col-span-7">
          <motion.div
            {...fade(0)}
            data-testid="hero-admissions-badge"
            className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-brand-terra/30 bg-brand-terra/5 px-4 py-2"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-terra opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-terra" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-terra">
              Admissions Open 2026–27 · Limited Seats
            </span>
          </motion.div>
          <motion.p
            {...fade(0.05)}
            className="font-mono text-xs uppercase tracking-[0.22em] text-brand-terra"
          >
            Est. 2008 · Dwarka, New Delhi
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="mt-7 font-serif text-5xl font-light leading-[1.08] tracking-tight md:text-6xl lg:text-7xl"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                custom={0}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
              >
                Shape Your Future
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="block"
                custom={1}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
              >
                in <em className="italic text-brand-terra">Global Hospitality</em>
              </motion.span>
            </span>
          </h1>

          <motion.p
            {...fade(0.65)}
            className="mt-8 max-w-xl text-base leading-relaxed text-brand-muted md:text-lg"
          >
            Ranked the #2 hotel management college in Delhi with a 100% placement
            record — UGC-recognised degrees and diplomas, international internships,
            and fees payable in easy EMIs.
          </motion.p>

          <motion.div {...fade(0.8)} className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-enquire-button"
              onClick={() => scrollTo("#enquire")}
              className="group inline-flex items-center gap-2 rounded-full bg-brand-green px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-ink"
            >
              Enquire Now
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-whatsapp-button"
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-bold text-white shadow-md transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#1DA851]"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp Us
            </a>
          </motion.div>

          <motion.div {...fade(0.95)} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2">
            {["Free career counselling", "EMI fee plans", "Campus visits available"].map((item) => (
              <span key={item} className="inline-flex items-center gap-1.5 text-sm text-brand-muted">
                <CheckCircle2 className="h-4 w-4 text-brand-green" />
                {item}
              </span>
            ))}
          </motion.div>

          <motion.div {...fade(1.05)} className="mt-7 flex flex-wrap gap-3 md:hidden">
            <a
              data-testid="hero-mobile-scholarship"
              href="/scholarship-exam"
              className="inline-flex items-center gap-2 rounded-full bg-brand-terra px-5 py-3 text-xs font-extrabold uppercase tracking-wide text-white shadow-md"
            >
              <Trophy className="h-4 w-4" /> Scholarship Exam
              <span data-testid="hero-scholarship-free-badge" className="rounded-full bg-white px-1.5 py-0.5 text-[9px] font-black leading-none tracking-wider text-brand-terra">FREE</span>
            </a>
            <a
              data-testid="hero-mobile-admission"
              href="/admission-form"
              className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-3 text-xs font-extrabold uppercase tracking-wide text-white shadow-md"
            >
              <GraduationCap className="h-4 w-4" /> Admission Form
            </a>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.45 }}
            className="relative"
          >
            <div className="aspect-[4/5] overflow-hidden rounded-md">
              <motion.img
                style={{ y: imgY }}
                src={IMAGES.hero}
                alt="ABHI students training in the campus culinary kitchen"
                className="h-[115%] w-full scale-105 object-cover"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 1.05 }}
              className="absolute -left-4 bottom-8 flex items-center gap-3 border border-white/40 bg-white/70 px-5 py-4 shadow-sm backdrop-blur-xl md:-left-10"
            >
              <BadgeCheck className="h-8 w-8 text-brand-green" />
              <div>
                <p className="font-serif text-lg leading-tight text-brand-ink">100% Placement</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">
                  Every batch · Since 2008
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        {...fade(1.0)}
        data-testid="stats-strip"
        className="border-t border-brand-line"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-2 md:grid-cols-4">
          {STATS.map((stat, i) => (
            <div
              key={stat.label}
              data-testid={`stat-${i}`}
              className={`px-6 py-8 ${i > 0 ? "border-l border-brand-line" : ""} ${
                i === 2 ? "max-md:border-l-0 max-md:border-t" : ""
              } ${i === 3 ? "max-md:border-t" : ""}`}
            >
              <p className="font-serif text-3xl text-brand-green md:text-4xl">{stat.value}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
